import { useCallback } from 'react';
import { useFetcher } from 'react-router';
import {
    MATCH_PROJECTS_API_ROUTE,
    type MatchProjectsResponse,
} from '~/routes/remix-api.get-matched.match-projects';
import type { MatchableProject } from '~/modules/get-matched/utils/match-buildhub-project';

interface UseBuildHubProjectsForMatchResult {
    projects: MatchableProject[];
    loading: boolean;
    fetchProjects: () => void;
}

/**
 * Loads the member's BuildHub projects for the matcher step via useFetcher.
 * Apollo isn't mounted in the React tree on category pages — only the Remix
 * loader/action context has it.
 *
 * `fetchProjects` reloads on every call so a reopened modal (the component
 * stays mounted while the dialog is closed) picks up freshly-created projects
 * rather than reusing the first load. The caller's open-effect gates it to the
 * open transition, so it isn't called on every render.
 */
export const useBuildHubProjectsForMatch =
    (): UseBuildHubProjectsForMatchResult => {
        const fetcher = useFetcher<MatchProjectsResponse>();

        // `fetcher.load` is referentially stable across renders, so we
        // deliberately omit `fetcher` from the deps to keep `fetchProjects`
        // stable for consumers that pass it to a useEffect dep array.
        const fetchProjects = useCallback(() => {
            fetcher.load(MATCH_PROJECTS_API_ROUTE);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return {
            projects: fetcher.data?.projects ?? [],
            loading: fetcher.state !== 'idle',
            fetchProjects,
        };
    };
